import { inject, Injectable, OnDestroy, signal, computed } from '@angular/core';
import { OKTA_AUTH } from '@okta/okta-angular';
import { CustomUserClaims, UserClaims } from '@okta/okta-auth-js';
import { HttpResponse } from '@angular/common/http';
import { firstValueFrom, BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from '@env/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AccountModel } from '@models/account.model';

const USER_DATA_KEY = '_mySoftProUserData';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  oktaAuth = inject(OKTA_AUTH);
  http = inject(HttpClient);

  private isLoadedSource = new BehaviorSubject<boolean>(false);
  isLoaded$ = this.isLoadedSource.asObservable();

  private userDataSource = new BehaviorSubject<any>(null);
  userData$ = this.userDataSource.asObservable();

  private _isSystemUser: boolean = false;
  private _isSystemPublic: boolean = false;
  private _isSystemAdmin: boolean = false;
  private _isAccountAdmin: boolean = false;
  private _isAccountBiller: boolean = false;
  private _isHostedPortalEditor: boolean = false;
  private _isHostedPortalAccountAdmin: boolean = false;
  private _hasAtleastOneAccountWithOnlineBillPay: boolean = false;
  private _canTransferToAtleastOneOBPPortal: boolean = false;
  private _selectedAccount: any = null;
  private _hasOnlineBillPayAccess: boolean = false;
  private _adminAccounts: AccountModel[] = [];
  private _selectedAccountSignal = signal<any>(null);
  public selectedAccount = computed(() => this._selectedAccountSignal());

  private destroy$ = new Subject<void>();

  async initializeApplication(): Promise<boolean> {
    // Get access token
    const token = this.getAccessToken();

    if (!token) {
      // Not authenticated, do not sign out here, let Okta Guard handle it.
      this.isLoadedSource.next(false);
      return true;
    }

    const userData = this.getJSONDataFromSession();

    if (userData) {
      this.saveDefaultValues(userData);
      this.isLoadedSource.next(true);
      return true;
    }

    // Fallback if we can't get user data.
    try {
      const initData = await firstValueFrom(this.initializeUser(token));
      if (initData && initData.userId) {
        this.saveUserDataToSession(initData);
        this.saveDefaultValues(initData);
        this.isLoadedSource.next(true);
        return true;
      }
    } catch (err) {
      console.error('initializeUser failed', err);
      // Decide: show an error, or trigger re-auth
      // this.isLoadedSource.next(false);
      // Optionally: await this.oktaAuth.signInWithRedirect();
    }

    // Fallback, try refresh
    try {
      const refreshed = await this.refreshUserData();
      if (refreshed && refreshed.userId) {
        this.saveUserDataToSession(refreshed);
        this.saveDefaultValues(refreshed);
        this.isLoadedSource.next(true);
        return true;
      }
    } catch (err) {
      console.error('refreshUserData failed (fallback)', err);
    }

    // Could not load
    this.isLoadedSource.next(false);
    return false;
  }

  // This API call sets up the users Session in the Web Api
  initializeUser(accessToken: string) {
    // Manually add token for first time access.
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${accessToken}`,
      }),
    };

    return this.http
      .get<any>(`${environment.apiUrl}/login/initialize`, httpOptions)
      .pipe((userData) => userData);
  }

  initializeDocsPortal(accessToken: string) {
    // Manually add token for first time access.
    const headers = new HttpHeaders({ Authorization: 'Bearer ' + accessToken });
    return this.http.get(`${environment.apiUrl}/login/docsportal`, {
      headers: headers,
      observe: 'response',
      responseType: 'text',
    });
  }

  goToDocsPortal(): Observable<HttpResponse<string>> {
    // Get token to go to docs portal.
    let token = this.getAccessToken();
    const headers = new HttpHeaders({ Authorization: 'Bearer ' + token });
    return this.http.get(`${environment.apiUrl}/login/docsportal`, {
      headers: headers,
      observe: 'response',
      responseType: 'text',
    });
  }

  // return this.http.get(`${environment.apiUrl}${path}`, {observe: 'response', responseType: 'text'});
  saveDefaultValues(userData: any): void {
    this._isSystemUser = false;
    this._isSystemPublic = false;
    this._isSystemAdmin = false;
    this._isAccountAdmin = false;
    this._isAccountBiller = false;
    this._isHostedPortalEditor = false;
    this._isHostedPortalAccountAdmin = false;
    this._hasAtleastOneAccountWithOnlineBillPay = false;
    this._canTransferToAtleastOneOBPPortal = false;
    this._hasOnlineBillPayAccess = false;
    this._adminAccounts = [];

    // Save system role information
    if (userData.systemRole) {
      this._isSystemUser = true;
      this._isSystemAdmin = userData.systemRole == 'Administrator';
      this._isHostedPortalEditor = userData.systemRole == 'Host Edit';
      this._isSystemPublic = userData.systemRole == 'Public';
    }

    this._hasOnlineBillPayAccess =
      userData.onlineBillPayAccessList?.length ?? 0;

    userData.accounts?.forEach((account: any) => {
      if (account.role == 'Administrator') {
        // Is an account admin for at least one account.
        this._isAccountAdmin = true;
        if (account.showHostedPortal == true) {
          // Has hosted portal for at least one account
          // And user is an admin of this account.
          this._isHostedPortalAccountAdmin = true;
        }

        // Save Account Admin Organizations
        let adminAccount = {
          accountId: account.accountId,
          display: `${account.name} (${account.serialNumber})`,
          serialNumber: account.serialNumber,
        };
        this._adminAccounts.push(adminAccount);
      } else if (account.role == 'Biller') {
        // Is a biller of at least one account
        this._isAccountBiller = true;
      }

      // Has an account with OBP
      if (!account.excludeFromOnlineBillPay && account.staxbillId) {
        this._hasAtleastOneAccountWithOnlineBillPay = true;
        if (account.role == 'Biller' || account.role == 'Administrator') {
          this._canTransferToAtleastOneOBPPortal = true;
        }
      }

      // save selected account
      // if (userData.selectedAccountId != null && userData.selectedAccountId == account.accountId) {
      //   this._selectedAccount = account;
      // }

      // save selected account
      if (userData.selectedAccountId != null && userData.accounts) {
        const acct =
          userData.accounts.find(
            (a: any) => a.accountId === userData.selectedAccountId,
          ) ?? null;
        this._selectedAccount = acct;
        this._selectedAccountSignal.set(acct);
      }
    });

    // Set user datasource
    this.userDataSource.next(userData);

    // Set a value indicating the data is loaded and ready to use.
    this.isLoadedSource.next(true);
  }

  // This api call will check the user token and refresh the session data if valid.
  async refreshUserData(): Promise<any> {
    return await firstValueFrom(
      this.http.get<any>(`${environment.apiUrl}/login/refreshdata`),
    );
  }

  async isAuthenticated(): Promise<boolean> {
    return await this.oktaAuth.isAuthenticated();
  }

  saveUserDataToSession(userData: any): void {
    // Save user data to session storage
    //sessionStorage.setItem("_mySoftProUserData", JSON.stringify(userData));
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
  }

  removeUserDataFromSession(): void {
    localStorage.removeItem(USER_DATA_KEY);
  }

  async waitUntilReady(): Promise<boolean> {
    return true;
  }

  async getUserDataFromSession(): Promise<any | null> {
    const cached = this.getJSONDataFromSession();
    if (cached) return cached;

    // Check authentication before trying a refresh
    const authed = await this.oktaAuth.isAuthenticated();
    if (!authed) {
      // Not authenticated, let component guards handle it.
      return null;
    }

    // Try to refresh from API
    try {
      const newData = await this.refreshUserData(); // may throw 401/Network
      if (newData && newData.userId) {
        this.saveUserDataToSession(newData);
        return newData;
      }
    } catch (err) {
      console.error('refreshUserData failed', err);
    }

    // If we got here, we are authenticated but couldn't fetch user data.
    // Clear any stale data and signal failure to the caller.
    this.removeUserDataFromSession();
    return null;
  }

  getJSONDataFromSession(): any {
    const userData = localStorage.getItem(USER_DATA_KEY);
    return userData ? JSON.parse(userData) : null;
  }

  async updateUserDataSession(userData: any): Promise<void> {
    this.saveUserDataToSession(userData);
    this.saveDefaultValues(userData);

    const acct =
      userData.accounts?.find(
        (a: any) => a.accountId === userData.selectedAccountId,
      ) ?? null;
    this._selectedAccount = acct;
    this._selectedAccountSignal.set(acct);
  }

  async updateSelectedAccountSession(selectedAccountId: string): Promise<void> {
    // Get User Data, update selected account and save back to session.
    let userData = await this.getUserDataFromSession();
    if (userData) {
      if (selectedAccountId != userData.selectedAccountId) {
        userData.selectedAccountId = selectedAccountId;
        this.saveUserDataToSession(userData);
        this.saveDefaultValues(userData);
      }
    }
  }

  async addSelectedAccountToSessionData(account: any): Promise<void> {
    let userData = await this.getUserDataFromSession();

    if (userData) {
      // check if account already exists
      if (
        !userData.accounts.some((x: any) => x.accountId == account.accountId)
      ) {
        userData.accounts.push(account);
        if (account.role == 'Administrator') {
          // Save Account Admin Organizations
          if (
            !this._adminAccounts.some(
              (x: any) => x.accountId == account.accountId,
            )
          ) {
            let adminAccount = {
              accountId: account.accountId,
              display: `${account.name} (${account.serialNumber})`,
              serialNumber: account.serialNumber,
            };
            this._adminAccounts.push(adminAccount);
          }
        }
      }
      userData.selectedAccountId = account.accountId;
      this.saveUserDataToSession(userData);
      this.saveDefaultValues(userData);
    }
  }

  getAccessToken(): string | undefined {
    return this.oktaAuth.getAccessToken();
  }

  async getOktaUser(): Promise<UserClaims<CustomUserClaims>> {
    // Fetch the logged-in user details (user's claims)
    // User full name is exposed as a property named 'name'
    return await this.oktaAuth.getUser();
  }

  async signOut(): Promise<boolean> {
    this.removeUserDataFromSession();
    await this.oktaAuth.signOut({
      postLogoutRedirectUri: window.location.origin,
    });
    return true;
  }

  async getAccounts(): Promise<any> {
    // this method handles authentication and signing out if needed.
    let sessionData = await this.getUserDataFromSession();
    return sessionData?.accounts ?? [];
  }

  public getAdminAccounts(): AccountModel[] {
    return this._adminAccounts;
  }

  public isSystemUser(): boolean {
    return this._isSystemUser;
  }

  public isSystemPublic(): boolean {
    return this._isSystemPublic;
  }

  public isSystemAdmin(): boolean {
    return this._isSystemAdmin;
  }

  public isHostedPortalEditor(): boolean {
    return this._isHostedPortalEditor;
  }

  public isAccountAdmin(): boolean {
    return this._isAccountAdmin;
  }

  public isBiller(): boolean {
    return this._isAccountBiller;
  }

  public hasAtleastOneAccountWithOnlineBillPay(): boolean {
    return this._hasAtleastOneAccountWithOnlineBillPay;
  }

  public canTransferToAtleastOneOBPPortal(): boolean {
    return this._canTransferToAtleastOneOBPPortal;
  }

  public isHostedPortalAccountAdmin(): boolean {
    return this._isHostedPortalAccountAdmin;
  }

  public showHostedPortal(): boolean {
    // Show the Hosted Portal Users link
    return (
      this._isSystemUser || // Has any system role
      this._isHostedPortalAccountAdmin
    ); // Is account admin to at least one account that has hosted portal active
  }

  public showMySoftProUsers(): boolean {
    return (
      this._isSystemUser || // Has any system role.
      this._isAccountAdmin
    ); // Is an account admin to at least one account.
  }

  public showOnlineBillPay(): boolean {
    return (
      this._isSystemUser || // Has any system role
      this._isAccountAdmin || // Is an account admin to at least one account.
      this._isAccountBiller || // Is an account biller to at least one account.
      this._hasOnlineBillPayAccess
    );
  }

  public showCreateUsersButton(): boolean {
    return (
      this._isSystemAdmin || // Has any system role.
      this._isAccountAdmin
    ); // Is an account admin to at least one account.
  }

  public canEditAllOrgRoles(): boolean {
    return this._isSystemAdmin || this._isSystemPublic;
  }

  public canEditAllHostedUsers(): boolean {
    return this._isSystemAdmin || this._isHostedPortalEditor;
  }

  // This method is to try to auto-refresh the user data in memory
  // for the BehaviorSubject if we still have the token and are still authenticated.
  refreshMemoryFromSession() {
    this.isAuthenticated().then(async (isAuthed) => {
      if (isAuthed) {
        // Returns user object already parsed by JSON.
        this.refreshUserData().then(async (newData) => {
          if (newData && newData.userId != 0) {
            this.saveUserDataToSession(newData);
            this.saveDefaultValues(newData);
            return newData;
          } else {
            await this.signOut();
          }
        });
      } else {
        await this.signOut();
      }
    });
  }

  async updateAccountSelection(
    accountId: string,
    isSysUser: boolean,
  ): Promise<void> {
    this.patchRequest(`/user/changeaccount/${accountId}`, null).subscribe(
      async (data: any) => {
        // This method returns true if the account was successfully set to the new selected account.
        // If the user is a system user and they select an account that they have no role in, it returns that account's data.
        if (isSysUser && data.accountId != null && accountId != '') {
          // Add the account to the user data for the system user if not already added.
          await this.addSelectedAccountToSessionData(data);
        } else {
          if (data) {
            // Update the local session data.
            await this.updateSelectedAccountSession(accountId);
          }
        }
      },
    );
  }

  patchRequest(path: string, data: any | null) {
    return this.http.patch<any>(`${environment.apiUrl}${path}`, data);
  }

  public async getAuthDomain(username: string): Promise<string> {
    var idpprovider = await firstValueFrom(
      this.http.get<any>(
        `${environment.oktaBaseUrl}${environment.oktaWebfingerUrl}${encodeURIComponent(username)}`,
      ),
    );
    return idpprovider.links[0].titles.und;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
